import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _PropTypes from 'prop-types';
import { useDragLayer } from 'react-dnd';
import styled from 'styled-components';
import { getDropShadowByElevation } from '../../styles/elevation.js';
import { spacing6x, spacing4x, spacing2x, getSpacing } from '../../styles/spacing.js';
import { Box } from '../Box/Box.js';
import { IconDrag } from '../Icons/IconDrag.js';
import { accordionDragType } from './AccordionDndContext.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var dragAccordionZIndex = 100;
var lineHeightValue = getSpacingValue(getSpacing(8));
var defaultBorderWidth = 2;
var StyledDragAccordionWrapper = styled.div.withConfig({
  displayName: "DragAccordion__StyledDragAccordionWrapper",
  componentId: "sc-pu38z-0"
})({
  position: "relative",
  height: 60,
  width: 320
});
var StyledDragAccordion = styled(Box).withConfig({
  displayName: "DragAccordion__StyledDragAccordion",
  componentId: "sc-pu38z-1"
})(["&&{display:flex;flex-direction:row;margin-bottom:", ";height:60px;width:320px;border-radius:4px;box-shadow:", ";", ";", ";}"], spacing6x, getDropShadowByElevation(1), function (_ref) {
  var theme = _ref.theme;
  return "background-color: ".concat(theme.neutralColors.white, ";");
}, function (_ref2) {
  var $variant = _ref2.$variant,
      theme = _ref2.theme;
  return $variant === "embedded" ? "\n            border: none;\n            border-top-color: ".concat(theme.neutralColors.silver, ";\n            border-top-width: 1px;\n            border-top-style: solid;\n            margin-bottom: 0;\n            &:first-of-type {\n                border: none;\n            }\n            zindex: ").concat(dragAccordionZIndex, ";\n            ") : "";
});
var StyledDragHandle = styled.div.withConfig({
  displayName: "DragAccordion__StyledDragHandle",
  componentId: "sc-pu38z-2"
})(function (_ref3) {
  var _ref3$borderWidth = _ref3.borderWidth,
      borderWidth = _ref3$borderWidth === void 0 ? defaultBorderWidth : _ref3$borderWidth,
      disabled = _ref3.disabled,
      isDragPlaceholder = _ref3.isDragPlaceholder,
      isDragPreview = _ref3.isDragPreview,
      theme = _ref3.theme;
  return _objectSpread(_objectSpread({}, function () {
    if (isDragPlaceholder) {
      return {
        opacity: 0
      };
    }
  }()), {}, {
    height: "".concat(60 + borderWidth, "px"),
    background: isDragPreview ? void 0 : theme.neutralColors.pearl,
    borderRadius: 2,
    cursor: !disabled ? "move" : "not-allowed",
    lineHeight: "".concat(lineHeightValue + borderWidth * 2, "px"),
    margin: -borderWidth,
    paddingLeft: spacing4x,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  });
});
StyledDragHandle.displayName = "StyledDragHandle";
var StyledDragHeader = styled.div.withConfig({
  displayName: "DragAccordion__StyledDragHeader",
  componentId: "sc-pu38z-3"
})(function (_ref4) {
  var theme = _ref4.theme;
  return {
    color: theme.neutralColors.charcoal,
    fontSize: "20px",
    lineHeight: "28px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "start",
    padding: spacing4x,
    paddingLeft: spacing2x,
    width: "100%"
  };
});

var DragAccordion = function DragAccordion(_ref5) {
  var $variant = _ref5.$variant;

  var _useDragLayer = useDragLayer(function (monitor) {
    return {
      currentOffset: monitor.getSourceClientOffset(),
      isDragging: monitor.isDragging(),
      item: monitor.getItem(),
      itemType: monitor.getItemType()
    };
  }),
      currentOffset = _useDragLayer.currentOffset,
      isDragging = _useDragLayer.isDragging,
      item = _useDragLayer.item,
      itemType = _useDragLayer.itemType;

  if (itemType === accordionDragType && isDragging) {
    return /*#__PURE__*/jsx("div", {
      style: {
        position: "fixed",
        pointerEvents: "none",
        zIndex: dragAccordionZIndex,
        insetInlineStart: 0,
        insetBlockStart: 0,
        width: "100%",
        height: "100%"
      },
      children: /*#__PURE__*/jsx(StyledDragAccordionWrapper, {
        style: {
          transform: "translate(".concat(currentOffset === null || currentOffset === void 0 ? void 0 : currentOffset.x, "px, ").concat(currentOffset === null || currentOffset === void 0 ? void 0 : currentOffset.y, "px)")
        },
        children: /*#__PURE__*/jsxs(StyledDragAccordion, {
          $variant: $variant,
          children: [/*#__PURE__*/jsx(StyledDragHandle, {
            isDragPreview: true,
            children: /*#__PURE__*/jsx(IconDrag, {
              size: 16
            })
          }), /*#__PURE__*/jsx(StyledDragHeader, {
            role: "heading",
            className: "semi-bold",
            children: item.headerContent
          })]
        })
      })
    });
  } else {
    return null;
  }
};

DragAccordion.propTypes = {
  "$variant": _PropTypes.oneOf(["embedded", "fullwidth", "sidepanel"])
};
DragAccordion.displayName = "DragAccordion";
var StyledDropBar = styled.div.withConfig({
  displayName: "DragAccordion__StyledDropBar",
  componentId: "sc-pu38z-4"
})(function (_ref6) {
  var isDragOver = _ref6.isDragOver,
      position = _ref6.position,
      theme = _ref6.theme;
  var padding = 4;
  return _objectSpread(_objectSpread({}, position === "top" ? {
    top: -padding - 1
  } : {
    bottom: -padding - 1
  }), {}, {
    opacity: isDragOver ? 1 : 0,
    className: "dropbar",
    padding: "".concat(padding, "px 0"),
    width: 320,
    zIndex: 2,
    "&::after": {
      content: "''",
      borderBottom: "dashed ".concat(defaultBorderWidth, "px ").concat(theme.CTAColors.primaryVariant),
      display: "block",
      height: 0
    }
  });
});
StyledDropBar.displayName = "StyledDropBar";

function getSpacingValue(spacing) {
  return parseInt(typeof spacing === "string" ? spacing : getSpacing(1), 10);
}

export { DragAccordion, StyledDragHandle, StyledDropBar };
